import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiUser, FiLock, FiMoon, FiSun, FiChevronRight, FiLogOut, FiTrash2, FiX } from 'react-icons/fi';
import { Button } from '@/components/common/Button';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import api from '@/utils/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-shrink: 0;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  flex: 1;

  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing.sm};
  padding-left: ${({ theme }) => theme.spacing.sm};

  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const SettingsSection = styled(motion.div)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(15, 23, 42, 0.6)'
      : '#f8fafc'};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.onBackground};
  font-size: ${({ theme }) => theme.fontSizes.base};
  transition: ${({ theme }) => theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.muted};
  }
`;

const ThemeToggleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const ThemeLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ThemeLabelText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const ThemeDescription = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
`;

const ThemeToggleButton = styled(motion.button)<{ $isDark: boolean }>`
  width: 60px;
  height: 32px;
  border-radius: 16px;
  background: ${({ $isDark }) => $isDark ? '#3b82f6' : '#cbd5e1'};
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.3s ease;

  &:hover {
    background: ${({ $isDark }) => $isDark ? '#2563eb' : '#94a3b8'};
  }
`;

const ThemeToggleKnob = styled(motion.div)<{ $isDark: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 4px;
  left: ${({ $isDark }) => $isDark ? '32px' : '4px'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: left 0.3s ease;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ $isDark }) => $isDark ? '#1e293b' : '#f59e0b'};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const SaveButton = styled(Button)`
  flex: 1;
`;

const CancelButton = styled(Button)`
  flex: 1;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.lg};
    padding: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SettingCard = styled(motion.div)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CardIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const CardContent = styled.div`
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const CardDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
  margin: 0;
  line-height: 1.5;
`;

const CardArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.muted};
  transition: ${({ theme }) => theme.transitions.default};

  svg {
    width: 20px;
    height: 20px;
  }

  ${SettingCard}:hover & {
    color: ${({ theme }) => theme.colors.primary};
    transform: translateX(4px);
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
      : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 400px;
  width: min(90%, 420px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    color: ${({ theme }) => theme.colors.onBackground};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ModalBody = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const WarningText = styled.p`
  color: ${({ theme }) => theme.colors.muted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

const ConfirmationInput = styled(Input)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const DeleteButton = styled(Button)`
  flex: 1;
  background: #ef4444;
  color: white;

  &:hover {
    background: #dc2626;
  }
`;

const CancelModalButton = styled(Button)`
  flex: 1;
`;

const DangerZone = styled.div`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(239, 68, 68, 0.05)'};
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const DangerZoneTitle = styled.h3`
  color: #ef4444;
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

const DangerZoneDescription = styled.p`
  color: ${({ theme }) => theme.colors.muted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  line-height: 1.5;
`;

const DeleteProfileButton = styled(Button)`
  width: 100%;
  background: #ef4444;
  color: white;

  &:hover {
    background: #dc2626;
  }
`;

type SettingsView = 'main' | 'profile' | 'password' | 'appearance';

export const SettingsPage = () => {
  const navigate = useNavigate();
  const { mode, toggleTheme } = useTheme();
  const { logout, user, updateUser, deleteProfile } = useAuth();
  
  // View state
  const [currentView, setCurrentView] = useState<SettingsView>('main');
  
  // Profile state
  const [fullName, setFullName] = useState(user?.name || 'John Doe');
  const [email, setEmail] = useState(user?.email || 'john.doe@example.com');
  const [phone, setPhone] = useState(user?.phone || '+1 234 567 8900');
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Delete profile state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleProfileSave = async () => {
    try {
      const response = await api.put('/auth/profile', {
        name: fullName,
        phone,
        // Email is not editable as it's the login ID
      });

      if (response.data.success) {
        // Update user context and local storage
        updateUser(response.data.data);
        toast.success('Profile updated successfully!');
        setCurrentView('main');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update profile';
      toast.error(message);
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match!');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long!');
      return;
    }
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }

    try {
      const response = await api.put('/auth/password', {
        currentPassword,
        newPassword,
      });

      if (response.data.success) {
        // Update token if provided
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        toast.success('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setCurrentView('main');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update password';
      toast.error(message);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        // Call logout API endpoint
        await api.post('/auth/logout');
      } catch (error) {
        // Continue with logout even if API call fails
        console.error('Logout API error:', error);
      } finally {
        // Clear local state and navigate
        logout();
        navigate('/login', { replace: true });
      }
    }
  };

  const handleDeleteProfile = async () => {
    if (deleteConfirmEmail !== user?.email) {
      toast.error('Email does not match. Please type your email correctly.');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteProfile(user?.email || '');
      toast.success('Profile deleted successfully');
      navigate('/login', { replace: true });
    } catch (error) {
      // Error is handled in deleteProfile
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmEmail('');
    }
  };

  const settingsCards = [
    {
      id: 'profile',
      icon: <FiUser />,
      title: 'Profile Information',
      description: 'Manage your personal information, email, and phone number',
      view: 'profile' as SettingsView,
    },
    {
      id: 'password',
      icon: <FiLock />,
      title: 'Reset Password',
      description: 'Change your password to keep your account secure',
      view: 'password' as SettingsView,
    },
    {
      id: 'appearance',
      icon: mode === 'dark' ? <FiMoon /> : <FiSun />,
      title: 'Appearance',
      description: 'Customize the look and feel of your application',
      view: 'appearance' as SettingsView,
    },
    {
      id: 'logout',
      icon: <FiLogOut />,
      title: 'Logout',
      description: 'Sign out from your account',
      view: 'main' as SettingsView,
      action: handleLogout,
    },
  ];

  const handleBackClick = () => {
    if (currentView === 'main') {
      navigate('/');
    } else {
      setCurrentView('main');
    }
  };

  return (
    <Container>
      <Header>
        <BackButton variant="outline" onClick={handleBackClick}>
          <FiArrowLeft /> Back
        </BackButton>
        <Title>Settings</Title>
      </Header>

      <Content>
        {currentView === 'main' && (
          <SettingsGrid>
            {settingsCards.map((card, index) => (
              <SettingCard
                key={card.id}
                onClick={() => card.action ? card.action() : setCurrentView(card.view)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileTap={{ scale: 0.98 }}
              >
                <CardHeader>
                  <CardIcon>{card.icon}</CardIcon>
                  <CardContent>
                    <CardTitle>{card.title}</CardTitle>
                    <CardDescription>{card.description}</CardDescription>
                  </CardContent>
                  <CardArrow>
                    <FiChevronRight />
                  </CardArrow>
                </CardHeader>
              </SettingCard>
            ))}
          </SettingsGrid>
        )}

        {currentView === 'profile' && (
          <SettingsSection
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <SectionHeader>
            <SectionIcon>
              <FiUser />
            </SectionIcon>
            <SectionTitle>Profile Information</SectionTitle>
          </SectionHeader>

          <FormGroup>
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email Address (Login ID)</Label>
            <Input
              id="email"
              type="email"
              value={email}
              readOnly
              disabled
              placeholder="Enter your email"
              style={{ cursor: 'not-allowed', opacity: 0.7 }}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
            />
          </FormGroup>

          <ButtonGroup>
            <SaveButton onClick={handleProfileSave}>
              Save Changes
            </SaveButton>
            <CancelButton 
              variant="outline" 
              onClick={() => {
                setFullName(user?.name || '');
                setEmail(user?.email || '');
                setPhone(user?.phone || '');
                setCurrentView('main');
              }}
            >
              Cancel
            </CancelButton>
          </ButtonGroup>

          <DangerZone>
            <DangerZoneTitle>Danger Zone</DangerZoneTitle>
            <DangerZoneDescription>
              Permanently delete your profile and all associated data. This action cannot be undone.
            </DangerZoneDescription>
            <DeleteProfileButton 
              onClick={() => setShowDeleteModal(true)}
            >
              <FiTrash2 style={{ marginRight: '8px' }} />
              Delete Profile
            </DeleteProfileButton>
          </DangerZone>
        </SettingsSection>
        )}

        {currentView === 'password' && (
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
          <SectionHeader>
            <SectionIcon>
              <FiLock />
            </SectionIcon>
            <SectionTitle>Reset Password</SectionTitle>
          </SectionHeader>

          <FormGroup>
            <Label htmlFor="currentPassword">Current Password</Label>
            <Input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </FormGroup>

          <ButtonGroup>
            <SaveButton onClick={handlePasswordReset}>
              Reset Password
            </SaveButton>
            <CancelButton 
              variant="outline" 
              onClick={() => {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setCurrentView('main');
              }}
            >
              Cancel
            </CancelButton>
          </ButtonGroup>
          </SettingsSection>
        )}

        {currentView === 'appearance' && (
          <SettingsSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
          <SectionHeader>
            <SectionIcon>
              {mode === 'dark' ? <FiMoon /> : <FiSun />}
            </SectionIcon>
            <SectionTitle>Appearance</SectionTitle>
          </SectionHeader>

          <ThemeToggleSection>
            <ThemeLabel>
              <ThemeLabelText>Theme Mode</ThemeLabelText>
              <ThemeDescription>
                {mode === 'dark' ? 'Dark mode is enabled' : 'Light mode is enabled'}
              </ThemeDescription>
            </ThemeLabel>

            <ThemeToggleButton
              $isDark={mode === 'dark'}
              onClick={toggleTheme}
              whileTap={{ scale: 0.95 }}
            >
              <ThemeToggleKnob $isDark={mode === 'dark'}>
                {mode === 'dark' ? <FiMoon /> : <FiSun />}
              </ThemeToggleKnob>
            </ThemeToggleButton>
          </ThemeToggleSection>
          </SettingsSection>
        )}
      </Content>

      {/* Delete Profile Modal */}
      {showDeleteModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowDeleteModal(false)}
        >
          <ModalContent
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Delete Profile</ModalTitle>
              <CloseButton onClick={() => setShowDeleteModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <WarningText>
                ⚠️ This action will permanently delete your profile and all associated data. This cannot be undone.
              </WarningText>
              <WarningText>
                To confirm, please type your email address:
              </WarningText>
              <ConfirmationInput
                type="email"
                placeholder={user?.email}
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              />
            </ModalBody>

            <ModalFooter>
              <CancelModalButton 
                variant="outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmEmail('');
                }}
                disabled={isDeleting}
              >
                Cancel
              </CancelModalButton>
              <DeleteButton
                onClick={handleDeleteProfile}
                disabled={isDeleting || deleteConfirmEmail !== user?.email}
              >
                {isDeleting ? 'Deleting...' : 'Delete Profile'}
              </DeleteButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};
